/**
 * What a "session" is: one visit, however many pages it covers.
 *
 * The SDK used to mint a new session id in its constructor — that is, on every
 * full page load. The server writes one `session_start` per session id, so a
 * person who read five pages was recorded as five visits, each one an arrival
 * of its own. Production, 2 Oct 2026: one contact showed 152 visits, and a
 * visitor who came from a post on X and read four pages became one X visit
 * and three "direct" ones, because the clicks inside the site have no outside
 * source. Every store page is a full page load, so Salla and Zid stores were
 * hit hardest.
 *
 * Now the id is kept in a first-party cookie (on the registrable domain, like
 * the visitor id, so `www.` and `app.` share it), and a new session starts
 * only when:
 *
 *   - the visitor has been inactive for 30 minutes (the cookie's own max-age,
 *     refreshed on activity — the same rule web analytics has always used), or
 *   - the page was reached through a tagged link: a UTM, an ad click id, a
 *     Hamla campaign link or a `?ref=`/`?via=` tag that differs from the one
 *     this session started with. That is a new arrival from a new source,
 *     and folding it into the old session would hand the old source the credit.
 *
 * An untagged outside referrer does NOT start a new session on its own. The
 * commonest one inside an active visit is a payment page sending the buyer
 * back to the store's thank-you page, and splitting the checkout there would
 * turn every paid order into a "direct" visit. An untagged return after 30
 * minutes is still a new session, by the timeout.
 *
 * The server needs no change: it already writes one `session_start` per
 * session id, so a stable id is all it takes for a visit to be counted once.
 */
import { CLICK_ID_PARAMS, TRACKING_PARAM } from './tracking-params';
import { readCookie, readLocal, writeCookie, writeLocal } from './visitor-id';

export const SESSION_KEY = 'hamla_session';

/** Inactivity that ends a session. */
export const SESSION_IDLE_SECONDS = 30 * 60;

/** Activity refreshes the cookie at most this often; a click storm is one write. */
const TOUCH_INTERVAL_MS = 60 * 1000;

/**
 * Query params that mean "this visitor just arrived from somewhere specific".
 * `hamla_vid` is deliberately absent: it names WHO, not where from.
 */
const ARRIVAL_PARAMS: readonly string[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  ...CLICK_ID_PARAMS,
  TRACKING_PARAM.CAMPAIGN_ID,
  'ref',
  'via',
];

interface StoredSession {
  id: string;
  /** The tagged arrival this session started with, or null for an untagged one. */
  source: string | null;
  /** Last activity, epoch ms. */
  at: number;
}

let lastTouch = 0;

function generateSessionId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `sess-${timestamp}-${random}`;
}

function parse(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof value.id !== 'string' || !value.id.startsWith('sess-')) return null;
    if (typeof value.at !== 'number') return null;
    return { id: value.id, source: typeof value.source === 'string' ? value.source : null, at: value.at };
  } catch {
    return null;
  }
}

/**
 * The live session, if there is one. The cookie expires by itself after the
 * idle time; localStorage (the fallback when cookies are off) does not, so its
 * age is checked here, and the cookie's too in case a clock moved.
 */
function readSession(now: number): StoredSession | null {
  const stored = parse(readCookie(SESSION_KEY)) ?? parse(readLocal(SESSION_KEY));
  if (!stored) return null;
  if (now - stored.at >= SESSION_IDLE_SECONDS * 1000) return null;
  return stored;
}

function writeSession(session: StoredSession): void {
  const raw = JSON.stringify(session);
  writeCookie(SESSION_KEY, raw, SESSION_IDLE_SECONDS);
  writeLocal(SESSION_KEY, raw);
  lastTouch = session.at;
}

/**
 * The tagged arrival on a URL, as one comparable string, or null when the URL
 * carries no tag. Order-stable, so the same link always yields the same value.
 */
export function arrivalSource(href: string | null | undefined): string | null {
  if (!href) return null;
  let params: URLSearchParams;
  try {
    params = new URL(href).searchParams;
  } catch {
    return null;
  }
  const parts: string[] = [];
  for (const key of ARRIVAL_PARAMS) {
    const value = params.get(key);
    if (value) parts.push(`${key}=${value}`);
  }
  return parts.length ? parts.join('&') : null;
}

function currentHref(): string | null {
  return typeof window !== 'undefined' ? window.location.href : null;
}

/**
 * The session id for this page load: the live one, or a new one when the
 * visitor has been idle or has just arrived through a new tagged link.
 */
export function resolveSessionId(options: { href?: string | null; now?: number } = {}): string {
  const now = options.now ?? Date.now();
  const source = arrivalSource(options.href === undefined ? currentHref() : options.href);
  const stored = readSession(now);

  const continues = stored !== null && (source === null || source === stored.source);
  const session: StoredSession = continues
    ? { ...stored, at: now }
    : { id: generateSessionId(), source, at: now };

  writeSession(session);
  return session.id;
}

/** Start a session unconditionally — for a browser handed to someone else. */
export function startNewSession(now: number = Date.now()): string {
  const session: StoredSession = { id: generateSessionId(), source: null, at: now };
  writeSession(session);
  return session.id;
}

/**
 * Activity on the page keeps the session alive. Throttled, and never steals
 * the slot from a newer session another tab has started since.
 */
export function touchSession(id: string, now: number = Date.now()): void {
  if (now - lastTouch < TOUCH_INTERVAL_MS) return;
  const stored = readSession(now);
  if (stored && stored.id !== id) return;
  writeSession({ id, source: stored?.source ?? null, at: now });
}
